import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SignaturePad.css';

interface SignaturePadProps {
  /** Current signature as a PNG data URL, or '' when empty. */
  value: string;
  /** Called with the new PNG data URL after each stroke, or '' when cleared. */
  onChange: (dataUrl: string) => void;
  readOnly?: boolean;
  width?: number;
  height?: number;
  ariaLabel?: string;
}

/**
 * Lightweight electronic-signature pad backed by a <canvas>. Emits the drawing as
 * a PNG data URL on each completed stroke (mouse/touch), so it works with autosave
 * flows. In read-only mode it just shows the saved signature image.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  readOnly = false,
  width = 260,
  height = 90,
  ariaLabel = 'Signature',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  // Configure the canvas stroke and paint any existing signature onto it.
  useEffect(() => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasInk(true);
      };
      img.src = value;
    }
    // Only repaint on mount / when switching editability; live edits are drawn directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  const pointFromEvent = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (readOnly) return;
    const ctx = canvasRef.current?.getContext('2d');
    const p = pointFromEvent(e);
    if (!ctx || !p) return;
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || readOnly) return;
    const ctx = canvasRef.current?.getContext('2d');
    const p = pointFromEvent(e);
    if (!ctx || !p) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };

  const commit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (hasInk) commit();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange('');
  };

  if (readOnly) {
    return value ? (
      <img src={value} alt={ariaLabel} className="signature-pad-image" style={{ maxWidth: `min(${width}px, 100%)`, maxHeight: height }} />
    ) : (
      <span className="signature-pad-empty">—</span>
    );
  }

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        className="signature-pad-canvas"
        width={width}
        height={height}
        aria-label={ariaLabel}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button type="button" className="signature-pad-clear" onClick={clear}>
        Clear
      </button>
    </div>
  );
};

export default SignaturePad;
